export type CloudinaryUploadFolders = 'logo' | 'open-graph' | 'users' | 'events' | 'uploads' | 'stories' | 'media'

// Server-side `UploadOperationEnum` value for each folder. Adding a folder without
// a matching server operation is a compile error here, not a runtime failure.
export const UPLOAD_OPERATIONS: Record<CloudinaryUploadFolders, string> = {
  logo: 'LOGO',
  'open-graph': 'OPEN_GRAPH',
  users: 'USERS',
  events: 'EVENTS',
  uploads: 'UPLOADS',
  stories: 'STORIES',
  media: 'MEDIA',
}

// Selects the upload operation (`folder`) and, for logo replacement, the
// organization the caller must own. Neither is forwarded to Cloudinary, and
// neither is `allowPdf`, which only lets a PDF past fileProblem (the new-event
// form's flyer panel, CVL-173).
export type UploadOptions = { folder: CloudinaryUploadFolders; organizationId?: string; resource_type?: string; allowPdf?: boolean }

// Everything Cloudinary needs besides the file itself. The server picks all of
// it — including the timestamp and the folder — and the browser forwards it
// untouched, so no client can sign a destination or a clock of its choosing.
export type UploadCredentials = {
  apiKey: string
  cloudName: string
  folder: string
  signature: string
  timestamp: number
}

// Cloudinary rejects images over 10 MB on our plan; checking before the upload
// gives the user the reason (with their file's size) instead of a generic
// failure after the wait.
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

const IMAGE_HINT = 'Please use an image file like JPG or PNG'
const PDF_PROBLEM = `PDFs can't be uploaded here. ${IMAGE_HINT} — a screenshot of the flyer works well.`
const VIDEO_PROBLEM = `Videos can't be uploaded here. ${IMAGE_HINT}.`
const FILE_TYPE_PROBLEM = `That type of file can't be uploaded here. ${IMAGE_HINT}.`
const SIGNED_OUT_PROBLEM =
  "You've been signed out, so the image can't be uploaded. Sign in again in a new tab, then come back and re-attach the image."
const UNREACHABLE_PROBLEM = "The upload couldn't start — we couldn't reach CivicLift. Check your internet connection and try again."
const SERVER_PROBLEM = "The upload couldn't start because of a temporary problem on our side. Please try again in a minute."
const NOT_ALLOWED_PROBLEM = "Your account isn't allowed to upload images here. If you think it should be, contact your community's curator."
const BLOCKED_PROBLEM =
  "The image couldn't reach our image service. This is usually a network problem or a workplace web filter — try again, or try a different network or device."
const GARBLED_PROBLEM = 'Something went wrong finishing the upload. Please try again.'
const TOO_LARGE_REJECTION = 'This image is too large to upload — the maximum is 10 MB. Try exporting a smaller version.'
const UNREADABLE_REJECTION = "This file doesn't seem to be a readable image. Re-save it as a JPG or PNG and try again."

// Extensions we confidently know are documents even when the browser reports
// no MIME type. Anything ambiguous (e.g. HEIC photos, which often arrive with
// an empty type on Windows) is let through — Cloudinary is the authority.
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'xls', 'xlsx', 'zip']

const extensionOf = (name: string): string => {
  const dotIndex = name.lastIndexOf('.')
  return dotIndex === -1 ? '' : name.slice(dotIndex + 1).toLowerCase()
}

const formatMb = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

const tooLargeProblem = (bytes: number): string =>
  `This image is too large to upload (${formatMb(bytes)}) — the maximum is 10 MB. Try exporting a smaller version.`

// Plain-language reason this file can't be uploaded, or null when it can.
export const fileProblem = (file: File, { allowVideo, allowPdf = false }: { allowVideo: boolean; allowPdf?: boolean }): string | null => {
  const type = file.type
  const extension = extensionOf(file.name)

  if (type === 'application/pdf' || (type === '' && extension === 'pdf')) {
    if (!allowPdf) return PDF_PROBLEM
    return file.size > MAX_IMAGE_BYTES ? tooLargeProblem(file.size) : null
  }

  const isVideo = type.indexOf('video') === 0
  const isImage = type.indexOf('image') === 0

  if (isVideo && !allowVideo) return VIDEO_PROBLEM
  if (!isImage && !isVideo && (type !== '' || DOCUMENT_EXTENSIONS.includes(extension))) return FILE_TYPE_PROBLEM
  if (!isVideo && file.size > MAX_IMAGE_BYTES) return tooLargeProblem(file.size)

  return null
}

// Failure with a message written for the person uploading, not for a developer.
class UploadProblem extends Error {
  userMessage: string

  constructor(userMessage: string, detail?: string) {
    super(detail || userMessage)
    this.userMessage = userMessage
  }
}

type CredentialsResponseBody = {
  data?: { uploadCredentialsCreate?: { credentials?: Partial<UploadCredentials> | null } | null } | null
  errors?: { message?: string; extensions?: { code?: string } }[]
}

// Server denial codes (see Uploads::IssueSignedCredentials) mapped to what the
// person uploading can actually do about them.
const DENIAL_PROBLEMS: Record<string, string> = {
  UNAUTHENTICATED: SIGNED_OUT_PROBLEM,
  FORBIDDEN: NOT_ALLOWED_PROBLEM,
  UNKNOWN_OPERATION: SERVER_PROBLEM,
}

export const UPLOAD_CREDENTIALS_MUTATION = `
  mutation UploadCredentialsCreate($operation: UploadOperationEnum!, $organizationId: ID) {
    uploadCredentialsCreate(operation: $operation, organizationId: $organizationId) {
      credentials {
        apiKey
        cloudName
        folder
        signature
        timestamp
      }
    }
  }
`

const isUploadCredentials = (value: Partial<UploadCredentials> | null | undefined): value is UploadCredentials =>
  !!value &&
  typeof value.apiKey === 'string' &&
  typeof value.cloudName === 'string' &&
  typeof value.folder === 'string' &&
  typeof value.signature === 'string' &&
  typeof value.timestamp === 'number'

// Asks the server to authorize this upload operation and, if allowed, to issue
// the signed parameters. Denials come back as GraphQL errors.
export const fetchUploadCredentials = async (folder: CloudinaryUploadFolders, organizationId?: string): Promise<UploadCredentials> => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''

  let response: Response
  try {
    response = await fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        query: UPLOAD_CREDENTIALS_MUTATION,
        variables: { operation: UPLOAD_OPERATIONS[folder], organizationId: organizationId ?? null },
      }),
    })
  } catch (error) {
    throw new UploadProblem(UNREACHABLE_PROBLEM, `credentials fetch failed: ${String(error)}`)
  }

  if (!response.ok) throw new UploadProblem(SERVER_PROBLEM, `credentials request returned ${response.status}`)

  let body: CredentialsResponseBody
  try {
    body = await response.json()
  } catch {
    throw new UploadProblem(SERVER_PROBLEM, 'credentials response was not JSON')
  }

  const credentials = body.data?.uploadCredentialsCreate?.credentials
  if (isUploadCredentials(credentials)) return credentials

  const errors = body.errors ?? []
  const denial = errors.map((e) => e.message ?? '').join('; ')
  const code = errors.map((e) => e.extensions?.code).find((c) => c && Object.hasOwn(DENIAL_PROBLEMS, c))
  throw new UploadProblem(code ? DENIAL_PROBLEMS[code] : SERVER_PROBLEM, denial || 'credentials response had no credentials')
}

type CloudinaryErrorBody = { error?: { message?: string } }

const safeJsonParse = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

// Translate Cloudinary's rejection reasons into plain words where we know them.
const rejectionProblem = (message: string | undefined): string => {
  if (!message) return GARBLED_PROBLEM
  if (/file size too large/i.test(message)) return TOO_LARGE_REJECTION
  if (/invalid image file|unsupported\s.*format/i.test(message)) return UNREADABLE_REJECTION
  return `The image was rejected: ${message}`
}

const reportUploadFailure = (userMessage: string, context: Record<string, unknown>) => {
  const rollbar = (window as unknown as { Rollbar?: { warning?: (message: string, extra: unknown) => void } }).Rollbar
  if (rollbar && typeof rollbar.warning === 'function') {
    rollbar.warning('Image upload failed', { userMessage, ...context })
  }
}

const cloudinaryTools = {
  // The callback always runs exactly once: (null, response) on success, or a
  // plain-language message on failure. It must never be skipped — the upload
  // fields re-enable their form's Submit button off of it.
  async upload(file: File, options: UploadOptions, cb: (error: string | null, response: unknown) => void): Promise<void> {
    let callbackRan = false
    const done = (error: string | null, response: unknown) => {
      if (callbackRan) return
      callbackRan = true
      cb(error, response)
    }
    const fail = (userMessage: string, context: Record<string, unknown>) => {
      reportUploadFailure(userMessage, { folder: options.folder, fileType: file.type, fileSize: file.size, ...context })
      done(userMessage, null)
    }

    try {
      const problem = fileProblem(file, { allowVideo: options.resource_type === 'video', allowPdf: options.allowPdf })
      if (problem) return fail(problem, { stage: 'validation' })

      const credentials = await fetchUploadCredentials(options.folder, options.organizationId)

      const url = `https://api.cloudinary.com/v1_1/${credentials.cloudName}/upload`
      const xhr = new XMLHttpRequest()
      const fd = new FormData()
      xhr.open('POST', url, true)
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return

        if (xhr.status === 0) return fail(BLOCKED_PROBLEM, { stage: 'transfer', status: 0 })

        const body = safeJsonParse(xhr.responseText)
        if (xhr.status === 200) {
          if (body) return done(null, body)
          return fail(GARBLED_PROBLEM, { stage: 'response', status: 200 })
        }

        const cloudinaryMessage = (body as CloudinaryErrorBody | null)?.error?.message
        fail(rejectionProblem(cloudinaryMessage), { stage: 'cloudinary', status: xhr.status, cloudinaryMessage })
      }

      // Only the server-signed parameters are sent alongside the file; the
      // operation-selecting options never reach Cloudinary.
      const unsigned: Omit<UploadOptions, 'folder' | 'organizationId' | 'allowPdf'> = { resource_type: options.resource_type }
      const unsignedOptions = Object.entries(unsigned).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== '',
      )
      fd.append('api_key', credentials.apiKey)
      fd.append('signature', credentials.signature)
      fd.append('file', file)
      fd.append('timestamp', String(credentials.timestamp))
      fd.append('folder', credentials.folder)
      unsignedOptions.forEach(([key, value]) => fd.append(key, value))
      xhr.send(fd)
    } catch (error) {
      if (error instanceof UploadProblem) {
        fail(error.userMessage, { stage: 'credentials', detail: error.message })
      } else {
        fail(SERVER_PROBLEM, { stage: 'unexpected', detail: String(error) })
      }
    }
  },
}

export default cloudinaryTools
